import {
  Add,
  Button,
  DefaultLmc,
  EmptyState,
  PageHeader,
  Tag
} from '@lmc/ui-kit'
import { ListItem, NotStartedIcon } from '@lmc/ui-kit/src/dataDisplay'
import { Text } from '@lmc/ui-kit/src/general'
import { Spinner } from 'components/generic'
import useContingencyPlansList from 'hooks/useContingencyPlanList'
import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useRef } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { ProfileWrapper } from '../../styledComponents'
import { getPlanSubtitle, isPlanOverdue } from '../../utils/utils'

const isDesktop = isBrowser || isTablet

const ResidentContingencyPlans = ({ testId }: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement | null>(null)
  const { id } = useParams()
  const { contingencyPlans, isLoading } = useContingencyPlansList({
    residentID: id
  })
  const { resident, isLoading: isResidentLoading } = useResident({ id })

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId
            ? `${testId}-contingency-plans-loading`
            : 'contingency-plans-loading'
        }
      />
    )
  }

  const pageHeaderTitle =
    isResidentLoading || !resident
      ? t(supConstants.FUTURE_PLANS)
      : `${resident?.name?.first} ${resident?.name?.last}'s ${t(
          supConstants.FUTURE_PLANS
        )}`

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={
        testId
          ? `${testId}-contingency-plans-wrapper`
          : 'contingency-plans-wrapper'
      }
    >
      <PageHeader
        sticky
        title={pageHeaderTitle}
        // This is a reused code snippet for the back button that's inside a header that's already inside the codebase. Might be a good idea to refactor it at some point
        onBack={
          !isDesktop
            ? () => navigate(`/residents/${id}/profile`, { replace: true })
            : undefined
        }
        data-testid={
          testId
            ? `${testId}-contingency-plans-header`
            : 'contingency-plans-header'
        }
      >
        <Link to="add">
          <Button
            variant="primary"
            type="submit"
            icon={<Add />}
            data-testid={
              testId
                ? `${testId}-contingency-plans-add-button`
                : 'contingency-plans-add-button'
            }
          >
            {t(supConstants.ADD_NEW_PLAN)}
          </Button>
        </Link>
      </PageHeader>
      {contingencyPlans?.length > 0 ? (
        <PlansListWrapper
          data-testid={
            testId
              ? `${testId}-contingency-plans-list`
              : 'contingency-plans-list'
          }
        >
          {contingencyPlans.map((plan, index) => (
            <Link key={plan.id} to={plan.id}>
              <ContingencyPlanListItem
                title={<PlanTitle weight="bold">{plan.planName}</PlanTitle>}
                subtitle={
                  <PlanSubtitle level={2}>{getPlanSubtitle(plan)}</PlanSubtitle>
                }
                icon={
                  <IconContainer>
                    <DefaultLmc />
                  </IconContainer>
                }
                rightChildren={
                  isPlanOverdue(plan.nextReviewDate) ? (
                    <Tag color="red">{t(supConstants.OVERDUE)}</Tag>
                  ) : null
                }
                data-testid={
                  testId
                    ? `${testId}-contingency-plan-item-${index}`
                    : `contingency-plan-item-${index}`
                }
              />
            </Link>
          ))}
        </PlansListWrapper>
      ) : (
        <EmptyState
          title={t(supConstants.NO_PLANS)}
          description={t(supConstants.NO_PLANS_DESCRIPTION)}
          icon={<NotStartedIcon />}
          data-testid={
            testId
              ? `${testId}-contingency-plans-empty-state`
              : 'contingency-plans-empty-state'
          }
        />
      )}
    </ProfileWrapper>
  )
}

const PlansListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 1080px;
  margin: 0 auto;
`
const ContingencyPlanListItem = styled(ListItem)`
  background-color: ${({ theme }) => theme.color.neutral.color11};
  border-radius: 16px;
  padding: 12px 16px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 4px 8px;
  }
`

const PlanTitle = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const PlanSubtitle = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background-color: ${(props) => props.theme.color.status.secondary};
  border-radius: 9px;
`

export default ResidentContingencyPlans
